import {ChangeDetectorRef, Injectable, signal} from '@angular/core';
import {BehaviorSubject, finalize, Observable, throwError} from 'rxjs';
import {ProductSubscription} from '../types/subscriptons.type';
import {MCActionsService} from './mc-base/mc-actions-service';
import {SettingsApiService} from './api/settings-api.service';
import {map, tap} from 'rxjs/operators';
import {UserInfoQuery} from '../common/elf-store/user-info/user-info.query';
import {UserApiService} from './api/users-api.service';
import {AccountManager, SubscriptionBillingDetailsAll} from '../../userway/sdk/UserWayApiClient';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService extends MCActionsService {
  hasTrialSubscription$ = new BehaviorSubject<boolean>(false);
  hasAccountManager: boolean;
  accountManagerData: AccountManager;
  accountManagerDataIsLoading: boolean = true;

  private readonly subscriptionsList = signal<ProductSubscription[]>(null);
  private readonly subscriptionsEstimateList = signal<Map<number, number>>(null);
  private readonly selectedSubscription$ = signal<ProductSubscription>(null);

  subscriptionsLoading = signal<boolean>(false);

  constructor(
    userInfoQuery: UserInfoQuery,
    private settingsApiService: SettingsApiService,
    private userApiService: UserApiService,
  ) {
    super(userInfoQuery);
    this.initialized$.next(true);
  }

  get subscriptions() {
    return this.subscriptionsList();
  }

  set subscriptions(value: ProductSubscription[]) {
    if (!value) return;
    this.subscriptionsList.set(value);
  }

  get estimates() {
    return this.subscriptionsEstimateList();
  }

  set estimates(value: Map<number, number>) {
    this.subscriptionsEstimateList.set(value);
  }

  set selectedSubscription(subscription: ProductSubscription) {
    this.selectedSubscription$.set(subscription);
    this.hasTrialSubscription$.next(!!subscription?.isPayOnTrialAvailable);
  }

  get selectedSubscription(): ProductSubscription {
    return this.selectedSubscription$();
  }

  checkTrialSubscription$(): Observable<boolean> {
    return this.hasTrialSubscription$.asObservable();
  }

  getSubscriptionsData(searchDomainValue: string = '') {
    this.subscriptions = null;
    this.subscriptionsLoading.set(true);
    return this.userApiService.getSubscriptions(searchDomainValue).pipe(
      tap(res => {
        this.subscriptions = this.transformSubscriptionArray(res.payload);
      }),
      finalize(() => {
        this.subscriptionsLoading.set(false);
      }),
    );
  }

  getSubscriptionEstimates(cdr: ChangeDetectorRef, subscriptions: ProductSubscription[] = this.subscriptions) {
    const estimateMap = new Map<number, number>();

    subscriptions.forEach(subscription => {
      if (subscription.uwSubscriptionCancelled) return;
      const data = {
        paymentProvider: subscription?.paymentProvider?.toLowerCase(),
        paymentProviderSubscriptionId: subscription.uwSubscriptionId,
      };

      this.userApiService.getSubscriptionsEstimate(data).subscribe(estimate => {
        estimateMap.set(subscription.userServiceId, estimate.data.estimatedSubscriptionAmount);
        this.estimates = estimateMap;
        cdr.markForCheck();
      });
    });
  }

  clearSubscriptionsData(): void {
    this.estimates = null;
    this.subscriptions = null;
  }

  getSubscription(subscriptionId: number): Observable<SubscriptionBillingDetailsAll> {
    return this.userApiService.getSubscription(subscriptionId).pipe(
      map(res => {
        return {...res.payload.subscription};
      }),
    );
  }

  getAccountManager(): Observable<void> {
    return this.settingsApiService.getAccountManager().pipe(
      map(res => {
        this.accountManagerData = res.owner;
        this.hasAccountManager = this.accountManagerData.email !== 'support@userway.org';
        this.accountManagerDataIsLoading = false;
      }),
    );
  }

  private getSubscriptionsByStatus(subscriptions: ProductSubscription[]) {
    const sortBySubscriptionId = (a: ProductSubscription, b: ProductSubscription) =>
      b.uwSubscriptionId - a.uwSubscriptionId;

    const active = subscriptions.filter(({uwSubscriptionCancelled}) => !uwSubscriptionCancelled);
    const canceled = subscriptions.filter(({uwSubscriptionCancelled}) => uwSubscriptionCancelled);

    return {active: active.sort(sortBySubscriptionId), canceled: canceled.reverse()};
  }

  private enhanceSubscription(subscription: ProductSubscription): ProductSubscription {
    subscription.productList = [];
    subscription.totalValue = 0;

    const allSites = subscription.products.flatMap(product =>
      product.sites.filter(site => site.host && site.subtotal > 0).map(site => site.site || site.host),
    );

    subscription.activeSites = [...new Set(allSites)];

    return subscription;
  }

  transformSubscriptionArray(subscriptions: ProductSubscription[]): ProductSubscription[] {
    if (!subscriptions?.length) {
      return [];
    }

    const {active, canceled} = this.getSubscriptionsByStatus(subscriptions);

    const enhancedActiveSubscriptions = active.map(this.enhanceSubscription);
    const enhancedCanceledSubscriptions = canceled.map(this.enhanceSubscription);

    return [...enhancedActiveSubscriptions, ...enhancedCanceledSubscriptions];
  }
}
